import apiClient from 'services/_infra/apiClient';

export async function adminStartService(name, reason = '') {
  try {
    const res = await apiClient.post(`/service-manager/admin/services/${name}/mark-running`, {
      reason: reason || null,
    });
    return res.data;
  } catch (err) {
    console.error(`Admin start failed for '${name}':`, err);
    throw err;
  }
}

export async function adminStopService(name, reason = '') {
  try {
    const res = await apiClient.post(`/service-manager/admin/services/${name}/mark-stopped`, {
      reason: reason || null,
    });
    return res.data;
  } catch (err) {
    console.error(`Admin stop failed for '${name}':`, err);
    throw err;
  }
}

// Remove Service
export async function adminRemoveService(pmId) {
  try {
    const res = await apiClient.delete(`/service-manager/admin/services/${pmId}`);
    return res.data;
  } catch (err) {
    console.error(`Error removing service '${pmId}':`, err);
    throw err;
  }
}

// Command History
export async function getCommandHistory(params = {}) {
  try {
    const res = await apiClient.get('/service-manager/admin/command-history', { params });
    return res.data;
  } catch (err) {
    console.error('Error fetching command history:', err);
    throw err;
  }
}

// Add Service
export async function adminAddService(name) {
  try {
    const res = await apiClient.put(`/service-manager/admin/services/${name}`);
    return res.data;
  } catch (err) {
    console.error(`Error adding service '${name}':`, err);
    throw err;
  }
}

// Workplaces (Fusion-managed deployments — Phase 1 read-only)
export async function getWorkplaces() {
  try {
    const res = await apiClient.get('/service-manager/admin/workplaces');
    return res.data;
  } catch (err) {
    console.error('Error fetching workplaces:', err);
    throw err;
  }
}

// Fusion-managed deployments — Phase 2 (service toggle, hot reload).
// `actor` is synthesized server-side by the Fusion proxy from the auth token,
// so the FE only needs to provide the optional reason.
export async function enableWorkplaceService(wpIndex, serviceName, reason = '') {
  try {
    const res = await apiClient.post(
      `/service-manager/admin/workplaces/${encodeURIComponent(wpIndex)}/services/${encodeURIComponent(serviceName)}/enable`,
      { reason: reason || null },
    );
    return res.data;
  } catch (err) {
    console.error(`Error enabling workplace service '${serviceName}' on wp ${wpIndex}:`, err);
    throw err;
  }
}

export async function disableWorkplaceService(wpIndex, serviceName, reason = '') {
  try {
    const res = await apiClient.post(
      `/service-manager/admin/workplaces/${encodeURIComponent(wpIndex)}/services/${encodeURIComponent(serviceName)}/disable`,
      { reason: reason || null },
    );
    return res.data;
  } catch (err) {
    console.error(`Error disabling workplace service '${serviceName}' on wp ${wpIndex}:`, err);
    throw err;
  }
}

// Fusion-managed deployments — Phase 3 (add workplace, restart required).
// Returns 202 Accepted with { success, restartRequired, workplaceIndex, restartHint }.
export async function addWorkplace(payload) {
  try {
    const res = await apiClient.post('/service-manager/admin/workplaces', payload);
    return res.data;
  } catch (err) {
    console.error('Error adding workplace:', err);
    throw err;
  }
}

// Fusion-managed deployments — Phase 4 (update / remove, restart required).
// PATCH body is a subset of { name, prefix, rootDir, mode, port, reason }.
export async function updateWorkplace(wpIndex, partial) {
  try {
    const res = await apiClient.patch(
      `/service-manager/admin/workplaces/${encodeURIComponent(wpIndex)}`,
      partial,
    );
    return res.data;
  } catch (err) {
    console.error(`Error updating workplace ${wpIndex}:`, err);
    throw err;
  }
}

// Fusion-managed deployments — Phase 6 (supervisor restart).
// POST { reason? } -> 202 (scheduled, PM2-managed) | 503 (not in PM2, manual).
// We pass validateStatus so axios doesn't throw on 503 — the structured
// payload carries `manualRestartHint` the FE needs to surface inline.
// Anything outside the 2xx/503 set still throws (5xx supervisor down etc).
export async function restartSupervisor(reason = '') {
  try {
    const res = await apiClient.post(
      '/service-manager/admin/supervisor/restart',
      { reason: reason || null },
      { validateStatus: (status) => (status >= 200 && status < 300) || status === 503 },
    );
    // axios attaches the status here so the caller can branch on 202 vs 503.
    return { status: res.status, body: res.data };
  } catch (err) {
    console.error('Error scheduling supervisor restart:', err);
    throw err;
  }
}

// DELETE — body carries the optional reason + optimistic-locking lockVersion.
// Fusion proxy synthesizes actor. Pass `lockVersion` to opt into the safety
// check; omit to skip (backwards-compat with pre-locking clients).
/**
 * @param {number} wpIndex - the workplace index in the supervisor config.
 * @param {import('pages/Overwatch/ServicesManager/types/Deployments.types').RemoveWorkplaceRequest} [options]
 *   The DELETE body that the caller controls: `reason` and the optional `lockVersion`.
 */
export async function removeWorkplace(wpIndex, { reason = '', lockVersion } = {}) {
  try {
    const body = { reason: reason || null };
    if (lockVersion != null) body.lockVersion = lockVersion;
    const res = await apiClient.delete(
      `/service-manager/admin/workplaces/${encodeURIComponent(wpIndex)}`,
      { data: body },
    );
    return res.data;
  } catch (err) {
    console.error(`Error removing workplace ${wpIndex}:`, err);
    throw err;
  }
}

// Service enablement, global scope.
//
// A global service runs as ONE shared instance per supervisor rather than one
// per deployment, so it has a single flag in `fusion_supervisor.global_options`
// and no per-workplace override. `serviceName` is the catalog base name from
// the workplaces listing (`globalServices[].name`), never `pm2Name` - real names
// contain spaces and slashes, hence the encoding.
//
// Reachability: the supervisor serves these and the Analytics Server proxies
// them for ProjectDocs, but the Fusion-Backend proxy has no route for them
// yet (nothing in its `src/features/ServiceManager/` mentions global scope).
// Until it does, these resolve 404. Callers must treat that as "this Fusion
// backend has not shipped the route" rather than as a failed action, so the
// feature lights up on the backend change alone.
export async function enableGlobalService(serviceName, reason = '') {
  try {
    const res = await apiClient.post(
      `/service-manager/admin/services/global/${encodeURIComponent(serviceName)}/enable`,
      { reason: reason || null },
    );
    return res.data;
  } catch (err) {
    console.error(`Error enabling global service '${serviceName}':`, err);
    throw err;
  }
}

export async function disableGlobalService(serviceName, reason = '') {
  try {
    const res = await apiClient.post(
      `/service-manager/admin/services/global/${encodeURIComponent(serviceName)}/disable`,
      { reason: reason || null },
    );
    return res.data;
  } catch (err) {
    console.error(`Error disabling global service '${serviceName}':`, err);
    throw err;
  }
}
